import type { AIDifficulty } from '../../shared/ai';
import type { GameState, PlayerId } from '../../shared/types/domain';
import type { ReadonlySignal } from '../reactive';
import { signal } from '../reactive';
import type { WaitingScreenState } from '../ui/screens';
import type { LogisticsStore } from './logistics-store';
import type { ClientState } from './phase';
import { createPlanningStore, type PlanningStore } from './planning';
import type { ReconnectOverlayState } from './session-ui-state';
import type { GameTransport } from './transport';

export type OnboardingEntry = 'training' | 'post_training';

const defineReactiveSessionProperty = <T>(
  session: object,
  key: string,
  initial: T,
): ReadonlySignal<T> => {
  const backingSignal = signal(initial);

  Object.defineProperty(session, key, {
    enumerable: true,
    configurable: false,
    get: () => backingSignal.value,
    set: (next: T) => {
      backingSignal.value = next;
    },
  });

  return backingSignal;
};

/** Single client-side session shape: connection, lobby, and in-match fields live here. */
export interface ClientSession {
  state: ClientState;
  readonly stateSignal: ReadonlySignal<ClientState>;
  playerId: PlayerId | -1;
  readonly playerIdSignal: ReadonlySignal<PlayerId | -1>;
  /** True while connected as a live spectator (`?viewer=spectator`). */
  spectatorMode: boolean;
  gameCode: string | null;
  readonly gameCodeSignal: ReadonlySignal<string | null>;
  scenario: string;
  gameState: GameState | null;
  readonly gameStateSignal: ReadonlySignal<GameState | null>;
  logisticsState: LogisticsStore | null;
  readonly logisticsStateSignal: ReadonlySignal<LogisticsStore | null>;
  isLocalGame: boolean;
  readonly isLocalGameSignal: ReadonlySignal<boolean>;
  /** Identifies the guided match or the first mission launched after it. */
  onboardingEntry: OnboardingEntry | null;
  /** Most recent guided-flight movement explanation, shown on the next turn. */
  trainingMovementFeedback: string | null;
  aiDifficulty: AIDifficulty;
  transport: GameTransport | null;
  planningState: PlanningStore;
  latencyMs: number;
  readonly latencyMsSignal: ReadonlySignal<number>;
  reconnectOverlayState: ReconnectOverlayState | null;
  readonly reconnectOverlayStateSignal: ReadonlySignal<ReconnectOverlayState | null>;
  opponentDisconnectDeadlineMs: number | null;
  readonly opponentDisconnectDeadlineMsSignal: ReadonlySignal<number | null>;
  waitingScreenState: WaitingScreenState | null;
  readonly waitingScreenStateSignal: ReadonlySignal<WaitingScreenState | null>;
  reconnectAttempts: number;
}

export const createInitialClientSession = (): ClientSession => {
  type ClientSessionDraft = Omit<
    ClientSession,
    | 'state'
    | 'stateSignal'
    | 'playerId'
    | 'playerIdSignal'
    | 'gameCode'
    | 'gameCodeSignal'
    | 'gameState'
    | 'gameStateSignal'
    | 'logisticsState'
    | 'logisticsStateSignal'
    | 'isLocalGame'
    | 'isLocalGameSignal'
    | 'latencyMs'
    | 'latencyMsSignal'
    | 'reconnectOverlayState'
    | 'reconnectOverlayStateSignal'
    | 'opponentDisconnectDeadlineMs'
    | 'opponentDisconnectDeadlineMsSignal'
    | 'waitingScreenState'
    | 'waitingScreenStateSignal'
  > & {
    state: ClientState;
    stateSignal: ReadonlySignal<ClientState>;
    playerId: PlayerId | -1;
    playerIdSignal: ReadonlySignal<PlayerId | -1>;
    gameCode: string | null;
    gameCodeSignal: ReadonlySignal<string | null>;
    gameState: GameState | null;
    gameStateSignal: ReadonlySignal<GameState | null>;
    logisticsState: LogisticsStore | null;
    logisticsStateSignal: ReadonlySignal<LogisticsStore | null>;
    isLocalGame: boolean;
    isLocalGameSignal: ReadonlySignal<boolean>;
    latencyMs: number;
    latencyMsSignal: ReadonlySignal<number>;
    reconnectOverlayState: ReconnectOverlayState | null;
    reconnectOverlayStateSignal: ReadonlySignal<ReconnectOverlayState | null>;
    opponentDisconnectDeadlineMs: number | null;
    opponentDisconnectDeadlineMsSignal: ReadonlySignal<number | null>;
    waitingScreenState: WaitingScreenState | null;
    waitingScreenStateSignal: ReadonlySignal<WaitingScreenState | null>;
  };

  const session = {
    spectatorMode: false,
    scenario: 'biplanetary',
    aiDifficulty: 'normal',
    onboardingEntry: null,
    trainingMovementFeedback: null,
    transport: null,
    planningState: createPlanningStore(),
    reconnectAttempts: 0,
  } as ClientSessionDraft;

  session.stateSignal = defineReactiveSessionProperty(session, 'state', 'menu');
  session.playerIdSignal = defineReactiveSessionProperty(
    session,
    'playerId',
    -1,
  );
  session.gameCodeSignal = defineReactiveSessionProperty(
    session,
    'gameCode',
    null,
  );
  session.gameStateSignal = defineReactiveSessionProperty(
    session,
    'gameState',
    null,
  );
  session.logisticsStateSignal = defineReactiveSessionProperty(
    session,
    'logisticsState',
    null,
  );
  session.isLocalGameSignal = defineReactiveSessionProperty(
    session,
    'isLocalGame',
    false,
  );
  session.latencyMsSignal = defineReactiveSessionProperty(
    session,
    'latencyMs',
    -1,
  );
  session.reconnectOverlayStateSignal = defineReactiveSessionProperty(
    session,
    'reconnectOverlayState',
    null,
  );
  session.opponentDisconnectDeadlineMsSignal = defineReactiveSessionProperty(
    session,
    'opponentDisconnectDeadlineMs',
    null,
  );
  session.waitingScreenStateSignal = defineReactiveSessionProperty(
    session,
    'waitingScreenState',
    null,
  );

  return session;
};

/** Subset read by WebSocket message handling (full `ClientSession` is assignable). */
export type ClientSessionMessageContext = Pick<
  ClientSession,
  | 'state'
  | 'playerId'
  | 'gameCode'
  | 'reconnectAttempts'
  | 'latencyMs'
  | 'gameState'
  | 'reconnectOverlayState'
  | 'opponentDisconnectDeadlineMs'
>;

/** Subset used by `applyClientStateTransition` (full `ClientSession` is assignable). */
export type ClientSessionStateTransitionContext = Pick<
  ClientSession,
  | 'state'
  | 'playerId'
  | 'gameCode'
  | 'gameState'
  | 'logisticsState'
  | 'planningState'
  | 'isLocalGame'
  | 'onboardingEntry'
  | 'trainingMovementFeedback'
>;

/** Merge defaults for tests and focused fakes. */
export const stubClientSession = (
  overrides: Partial<
    Omit<
      ClientSession,
      | 'stateSignal'
      | 'playerIdSignal'
      | 'gameCodeSignal'
      | 'gameStateSignal'
      | 'logisticsStateSignal'
      | 'isLocalGameSignal'
      | 'latencyMsSignal'
      | 'reconnectOverlayStateSignal'
      | 'opponentDisconnectDeadlineMsSignal'
      | 'waitingScreenStateSignal'
    >
  > = {},
): ClientSession => Object.assign(createInitialClientSession(), overrides);
